import { createSlice, PayloadAction, prepareAutoBatched } from '@reduxjs/toolkit';
import {
  CanvasTilesCoords,
  CopyWellDetail,
  LabelDto,
  NavigatorImagesData,
  ScoringWell,
  WellDetailsStates,
} from 'business/models/CustomModels';
import { DrawingLayer } from 'business/models/DrawingLayer';
import {
  DisputedScoresIds,
  ExperimentInfoDTO,
  ExperimentInfoImagingScheduleDTO,
  ExperimentTuningValuesDTO,
  GridLayerDTO,
  IExperimentInfoImagingScheduleDTO,
  IExperimentScreenLot,
  InspectionSetDTO,
  IPlateCrystalIdentifiers,
  IScoresGroupsDTO,
  LayerDTO,
  LayerStateDTO,
  LayerTypeDTO,
  PlateAtImagerResponseDTO,
  PlatesImagingTaskDTO,
  RandomLayerDTO,
  RandomLayerIngredientGroupDTO,
  ScoresOfWellDropsDTO,
  State,
  WellDrawingDetailDTO,
} from 'business/models/Models';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import PlateDtoClient from 'business/models/PlateDtoClient';
import {
  createPlateLables,
  getWellAndCanvasLabels,
  WellLabelType,
} from 'components/commandpanel/Navigator/DRCIPWUtil';
import { getExperimentPlateSize } from 'components/main/Experiment';
import { EOverlaySelectionType } from 'enums/EOverlaySelectionType';
import {
  GetContainers,
  GetExpInspections,
  getLinkedLayers,
  isNullOrUndefined,
  showAlertSnackBar,
} from 'utils/Global';
import { INTRESTINGSCOREID } from './plateReducer';
import EScoreType from 'enums/EScoreType';
import {
  applyDoeIngredientsData,
  getNewSelectedLayerAfterLayerDeletion,
  makeNewParentLinkedLayer,
  validateWellsAndDrops,
} from 'redux/utils/experiment';
import {
  generateEmptyCanvasItems,
  getNewPlate,
  getPlateStructure,
} from 'utils/ExperimentUtils';
import { dirtifyOverLappingLayers } from 'business/plateviewer/layer/ResizeLayerTools';
import { generate, unGenerateLayer } from 'business/plateviewer/Generate/GenerateCommon';
import { cloneDeep } from 'lodash';
import { getPlainArray } from 'transformers/utils';
import {
  Annotations,
  Containers,
  DetailExperimentDTO,
  DetailExpInfoDTO,
  ExperimentInfoDto,
  Plate,
  PlateCompositeImage,
  PlateDrawingIdentifiers,
  PlateImageStructure,
  ContainerDrop,
  PlateIngredient,
  PlateScribble,
  RegionCaptureProfilesDto,
  SaveExperimentInfo,
  ScoresOfWellDrops,
  DropImageParameters,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import { pasteNewLayerUtil } from 'components/plateviewer/experiment/PasteLayerHelper';
import {
  fetchAllCompositeImagesThunk,
  fetchDrawingIdentifiersThunk,
} from 'redux/thunks/experiment';
import {
  DrawingType,
  ScribbleUpdateDetails,
} from 'components/well/drawingManager/constants';
import { updateDrawingThunk } from 'redux/thunks/well';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';

export type ExperimentDataState = {
  expData: ExperimentDtoClient;
  originalExpData: ExperimentDtoClient;
  past: ExperimentDtoClient[];
  future: ExperimentDtoClient[];
  selectedLayerId: number;
  dropImgUrl: string;
  isDropImageAvailable: boolean;
  selectOverlay: EOverlaySelectionType;
  drawingMoveToBack: boolean;
  isLockDrawing: boolean;
  scoresOptionsList: IScoresGroupsDTO[];
  isLayerSelected: boolean;
  shouldShowLayerIngredientPanel: boolean;
  shouldShowLayerEditPanel: boolean;
  platesAtImagerStatus: PlateAtImagerResponseDTO[];
  motherPlatesScoringStatuses: { [key: string]: boolean };
  pastLayerSelectionState: any[];
  futureLayerSelectionState: any[];
  currentWellScores: Array<any>;
  scoringWell: any;
  plateImageStructure: PlateImageStructure[];
  wellSelectionDetails: WellSelectionDetails;
  isFetchingComposite: boolean;
  allowLayerEditing: boolean;
  isUpdatingGlobalData?: boolean;
  scoreExperimentWellsInProgressCount: number;
  isFetchingExperimentData: boolean;
  isLoadingComposite: boolean;
  isInitialCompositeLoaded: boolean;
};

export type WellSelectionDetails = {
  wellNavigationDetails: WellNavigationDetails[];
  dropImageParameters: DropImageParameters[];
  selectedDropImageParameters: DropImageParameters;
};

export type PlateScoresUpdateDTO = {
  plate: PlateDtoClient;
  scores: ScoresOfWellDropsDTO[][];
};

export type PlateImageUpdateDTO = {
  plate: Plate;
  image: any;
};
export interface ImagingScheduleUpdateDTO
  extends Omit<IExperimentInfoImagingScheduleDTO, 'selectedImagingScheduleId'> {
  selectedImagingScheduleId?: number;
}
const initialState = {
  expData: null,
  past: [],
  future: [],
  isLayerSelected: false,
  shouldShowLayerIngredientPanel: false,
  shouldShowLayerEditPanel: false,
  pastLayerSelectionState: [],
  futureLayerSelectionState: [],
  wellSelectionDetails: {
    wellNavigationDetails: [],
    dropImageParameters: [],
    selectedDropImageParameters: {},
  },
  isFetchingComposite: false,
  allowLayerEditing: true,
  isUpdatingGlobalData: false,
  scoreExperimentWellsInProgressCount: 0,
  isFetchingExperimentData: true,
  isLoadingComposite: true,
  isInitialCompositeLoaded: false,
} as ExperimentDataState;

const experimentDataSlice = createSlice({
  name: 'experiment',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchDrawingIdentifiersThunk.fulfilled, (state, action) => {
      experimentDataSlice.caseReducers.setPlateDrawingIdentifiers(state, {
        type: 'setPlateDrawingIdentifiers',
        payload: action.payload,
      });
    });
    builder.addCase(fetchAllCompositeImagesThunk.pending, (state, action) => {
      state.isFetchingComposite = true;
      if (action.meta.arg.regenerate) {
        state.plateImageStructure?.every((x) => (x.imageCompositeKeyString = '---'));
      }
    });
    builder.addCase(fetchAllCompositeImagesThunk.fulfilled, (state, action) => {
      const compositeImages = action.payload;
      compositeImages.forEach((compositeImage) => {
        if (action.meta.arg.regenerate) {
          setReplaceComposteImageStoreImageByKey(
            compositeImage.compositeImageKey,
            compositeImage.image,
          );
          showAlertSnackBar(
            EAlertType.SUCCESS,
            ESnackBarPosition.TOP,
            'Composite image has been updated.',
          );
        } else {
          setComposteImageStoreImageByKey(
            compositeImage.compositeImageKey,
            compositeImage.image,
          );
        }
        const plate = state.plateImageStructure.find(
          (x) => x.plateId == compositeImage.plateId,
        );
        plate.imageCompositeKeyString = compositeImage.compositeImageKey;
        state.isFetchingComposite = false;
        // Separate state to avoid loading shown every single time a composite is fetched
        if (!state.isInitialCompositeLoaded) {
          state.isInitialCompositeLoaded = true;
        }
      });
    });
    builder.addCase(updateDrawingThunk.fulfilled, (state, action) => {
      const { plateId, drawingType, drawingId, drawingUpdateDetails } = action.meta.arg;
      if (drawingType === DrawingType.Scribble) {
        const plateIndex = state.expData?.plateDto?.findIndex(
          (p) => p.plateId === plateId,
        );
        if (plateIndex === -1) {
          return;
        }
        const drawing = state.expData?.plateDto?.[
          plateIndex
        ]?.drawingIdentifiers?.plateScribblesDetail?.plateScribbles?.find(
          (s) => s.scribbleStartPointsXYofWell.id === drawingId,
        );
        if (!drawing) {
          return;
        }
        drawing.scribbleStartPointsXYofWell.color = (
          drawingUpdateDetails as ScribbleUpdateDetails
        ).color;
      }
    });
  },
  reducers: {
    updateExperimentDataState: {
      reducer: (state, action: PayloadAction<ExperimentDtoClient>) => {
        state.expData = { ...state.expData, ...action.payload } as ExperimentDtoClient;
      },
      prepare: prepareAutoBatched<ExperimentDtoClient>(),
    },
    updateExperimentLayers: (state, action: PayloadAction<DrawingLayer[]>): void => {
      state.expData.layers = [...action.payload];
      experimentDataSlice.caseReducers.dirtifyExperiment(state, {
        type: 'dirtifyExperiment',
        payload: true,
      });
    },
    updateSelectedLayer: (state, action: PayloadAction<DrawingLayer>): void => {
      const selectedLayerIndex = state.expData.layers.findIndex(
        (lyr) => lyr.id === action.payload.id,
      );
      if (selectedLayerIndex >= 0) {
        state.expData.layers[selectedLayerIndex] = action.payload;
      }
    },

    updateLayerGroupData: (
      state,
      action: PayloadAction<{ layerId: number; groups: RandomLayerIngredientGroupDTO[] }>,
    ) => {
      const { layerId, groups } = action.payload;

      const selectedLayerIndex = state.expData.layers.findIndex(
        (lyr) => lyr.id === layerId,
      );

      if (selectedLayerIndex >= 0) {
        const lyr = state.expData.layers.find((l) => l.id === layerId);
        if (lyr) {
          (lyr.layerDetail as RandomLayerDTO).randomLayerIngredientGroup = groups;
          lyr.dirty = true;
        }
        const layers = state.expData.layers;

        layers[selectedLayerIndex] = lyr;
        state.expData.isDirty = true;
        state.expData.layers = [...layers];
      }
    },

    replaceSelectedLayerDetail: (state, action): void => {
      const selectedLayerIndex = state.expData.layers.findIndex(
        (lyr) => lyr.id === action.payload.layerId,
      );
      if (selectedLayerIndex >= 0) {
        state.expData.layers[selectedLayerIndex].layerDetail = action.payload;
        state.expData.layers[selectedLayerIndex].dirty = true;
        experimentDataSlice.caseReducers.updateExperimentLayers(state, {
          type: 'updateExperimentLayers',
          payload: state.expData.layers as DrawingLayer[],
        });
      }
    },
    setSelectedLayerId: (state, action: PayloadAction<number>): void => {
      state.selectedLayerId = action.payload;
      state.isLayerSelected = true;
    },
    setUnselectLayer: (state): void => {
      state.selectedLayerId = -1;
      state.isLayerSelected = false;
    },
    setExpPlates: (state, action: PayloadAction<Plate[]>): void => {
      state.expData.plateDto = [...action.payload] as Plate[];
    },
    setExpRegionCaptureProfiles: (
      state,
      action: PayloadAction<RegionCaptureProfilesDto[]>,
    ): void => {
      state.expData.regionCaptureProfiles = [
        ...action.payload,
      ] as RegionCaptureProfilesDto[];
    },
    setExpPlate: (state, action: PayloadAction<Plate>): void => {
      const index = state.expData.plateDto.findIndex(
        (plate) => plate.plateId == action.payload.plateId,
      );
      state.expData.plateDto[index] = { ...action.payload } as Plate;
    },

    setExpPlateImage: (state, action: PayloadAction<PlateImageUpdateDTO>): void => {
      const index = state.expData.plateDto.findIndex(
        (plate) => plate.plateId == action.payload.plate.plateId,
      );
      const plate = { ...state.expData.plateDto[index] };
      plate.image = action.payload.image;
      state.expData.plateDto[index] = plate;
    },

    setExpPlateTilesCoords: (state, action: PayloadAction<CanvasTilesCoords>): void => {
      const index = state.expData.plateDto.findIndex(
        (plate) => plate.plateId == action.payload.plateId,
      );
      state.expData.plateDto[index] = {
        ...state.expData.plateDto[index],
        ...action.payload,
      } as Plate;
    },
    updateLayersCanvasData: (state, action: PayloadAction<DrawingLayer[]>): void => {
      for (let i = 0; i < action.payload.length; i++) {
        const index = state.expData.layers.findIndex((l) => l.id == action.payload[i].id);
        if (index < 0) {
          continue;
        }
        state.expData.layers[index].tabRect = { ...action.payload[i].tabRect };
        state.expData.layers[index].tabNameRect = action.payload[i].tabNameRect;
        state.expData.layers[index].layerRect = action.payload[i].layerRect;
        state.expData.layers[index].isSaveAttempted = action.payload[i].isSaveAttempted;
        state.expData.layers[index].hasError = action.payload[i].hasError;
      }
    },
    unDirtifyAlllayers: (state, action: PayloadAction<void>): void => {
      for (let i = 0; i < state.expData.layers.length; i++) {
        if (state.expData.layers[i].dirty) {
          state.expData.layers[i].layerState =
            state.expData.layers[i].id > 0 ? LayerStateDTO.Modified : LayerStateDTO.New;
        }
        state.expData.layers[i].dirty = false;
      }
    },
    setDropImageUrl: (state, action: PayloadAction<string>): void => {
      state.dropImgUrl = action.payload;
    },
    setExpTuningValues: (
      state,
      action: PayloadAction<ExperimentTuningValuesDTO[]>,
    ): void => {
      state.expData.experimentTuningValues = [...action.payload];
    },
    setIsDropImageAvailable: (state, action: PayloadAction<boolean>): void => {
      state.isDropImageAvailable = action.payload;
    },
    setSelectOverlay: (state, action: PayloadAction<EOverlaySelectionType>): void => {
      state.selectOverlay = action.payload;
    },
    setDrawingMoveToBack: (state, action: PayloadAction<boolean>): void => {
      state.drawingMoveToBack = action.payload;
    },
    setIsLockDrawing: (state, action: PayloadAction<boolean>): void => {
      state.isLockDrawing = action.payload;
    },
    dirtifyExperiment: (state, action: PayloadAction<boolean>): void => {
      state.expData.isDirty = action.payload;
    },
    setPlateTunedImage: (
      state,
      action: PayloadAction<{ image: any; plateId: number }>,
    ): void => {
      const index = state.expData.plateDto.findIndex(
        (p) => p.plateId === action.payload.plateId,
      );
      state.expData.plateDto[index].tunedImage = action.payload.image;
    },
    setScoreOptionsList: (state, action: PayloadAction<IScoresGroupsDTO[]>): void => {
      state.scoresOptionsList = action.payload;
    },
    //perf-effeort
    preserveUndoableState: (state): void => {
      if (state.expData) {
        state.past = [...state.past, cloneDeep(state.expData)];
      }

      state.pastLayerSelectionState = [
        ...state.pastLayerSelectionState,
        state.isLayerSelected.toString(),
      ];
    },
    performUndo: (state): void => {
      if (!state.past || !state.past.length) {
        return;
      }

      const newpresent = state.past[state.past.length - 1];
      const newPast = state.past.slice(0, state.past.length - 1);

      const newFuture = [state.expData, ...state.future];

      state.past = [...newPast];
      state.expData = { ...newpresent };
      state.future = [...newFuture];

      const newPresentLayerStatus =
        state.pastLayerSelectionState[state.pastLayerSelectionState.length - 1];
      const newPastLayerStatus = state.pastLayerSelectionState.slice(
        0,
        state.pastLayerSelectionState.length - 1,
      );
      const newFutureLayerStatus = [
        state.isLayerSelected.toString(),
        ...state.futureLayerSelectionState,
      ];

      state.pastLayerSelectionState = [...newPastLayerStatus];
      state.isLayerSelected = newPresentLayerStatus === 'true';
      state.futureLayerSelectionState = [...newFutureLayerStatus];
    },
    performRedo: (state): void => {
      if (!state.future || !state.future.length) {
        return;
      }

      const next = state.future[0];
      const newFuture = state.future.slice(1);
      state.past = [...state.past, state.expData];
      state.expData = next;
      state.future = newFuture;

      const nextLayerStatus = state.futureLayerSelectionState[0];
      const newFutureLayerStatus = state.futureLayerSelectionState.slice(1);
      state.pastLayerSelectionState = [
        ...state.pastLayerSelectionState,
        state.isLayerSelected.toString(),
      ];
      state.isLayerSelected = nextLayerStatus === 'true';
      state.futureLayerSelectionState = newFutureLayerStatus;
    },
    setIsLayerSelected: (state, action: PayloadAction<boolean>): void => {
      state.isLayerSelected = action.payload;
    },
    setShouldShowLayerIngredientPanel: (state, action: PayloadAction<boolean>): void => {
      state.shouldShowLayerIngredientPanel = action.payload;
    },
    setShouldShowLayerEditPanel: (state, action: PayloadAction<boolean>): void => {
      state.shouldShowLayerEditPanel = action.payload;
    },
    clearUndoRedoStack: (state) => {
      state.past = [];
      state.future = [];
      state.pastLayerSelectionState = [];
      state.futureLayerSelectionState = [];
    },
    setPlatesAtImagerStatus: (
      state,
      action: PayloadAction<PlateAtImagerResponseDTO[]>,
    ): void => {
      state.platesAtImagerStatus = action.payload;
    },
    setMotherPlatesScoringStatuses: (
      state,
      action: PayloadAction<{ [key: string]: boolean }>,
    ): void => {
      state.motherPlatesScoringStatuses = action.payload;
    },
    setDispensingRobotId: (state, action: PayloadAction<number>): void => {
      state.expData.info.dispensingRobotId = action.payload;
    },

    /**
     * RSRRFR
     * TODO: Need to avoid creating a new instances of state, for this we need to look into the useEffects
     * and choose its dependencies accordingly
     */
    setPlateLabels: (state, action: PayloadAction<LabelDto[]>) => {
      state.expData = { ...state.expData, plateLables: action.payload };
    },
    setWellLabels: (state, action: PayloadAction<WellLabelType[]>) => {
      state.expData = { ...state.expData, wellLables: action.payload };
    },
    setAllPlatesScores: (
      state,
      action: PayloadAction<{ [key: string]: ScoresOfWellDrops[][] }>,
    ): void => {
      const allScores = action.payload;
      const updatedPlateDto = state.expData.plateDto?.map((plate) => {
        const plateId = plate.plateId;
        if (allScores[plateId]) {
          return {
            ...plate,
            plateScores: allScores[plateId],
          };
        }
        return plate;
      });

      state.expData.plateDto = updatedPlateDto;
    },
    updatePlateBarcodes: (
      state,
      action: PayloadAction<{ plateId: number; barcode: string }[]>,
    ): void => {
      const updates = new Map(action.payload.map((item) => [item.plateId, item.barcode]));
      state.expData.plateDto = state.expData.plateDto?.map((plate) => {
        const newBarcode = updates.get(plate.plateId);
        if (newBarcode === undefined) {
          return plate;
        }

        return {
          ...plate,
          barCode: newBarcode,
          plateBarName: `${plate.plateNumber}, ${plate.plateId}, ${newBarcode}`,
        };
      });
    },
    setScoresOfCurrentWell: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNum: number;
        wellScores: ScoresOfWellDrops[];
      }>,
    ) => {
      const updatedPlateDto = state?.expData?.plateDto?.map((plate) => {
        if (plate.plateId === action.payload.plateId) {
          const updatedScores = plate.plateScores || [];
          const scoreIndex = updatedScores.findIndex(
            (scores) => scores[0]?.wellNumber === action.payload.wellNum,
          );
          if (!action.payload.wellScores?.length && scoreIndex >= 0) {
            updatedScores.splice(scoreIndex, 1);
          } else {
            if (scoreIndex >= 0) {
              updatedScores[scoreIndex] = action.payload.wellScores;
            } else {
              updatedScores.push(action.payload.wellScores);
            }
          }
          return {
            ...plate,
            plateScores: updatedScores,
          };
        }
        return plate;
      });

      state.expData.plateDto = updatedPlateDto;
    },
    toggleScoringWell: (state, action: PayloadAction<boolean>): void => {
      state.scoringWell = !state.scoringWell;
    },
    setCurrentWellScores: (state, action: PayloadAction<Array<any>>): void => {
      state.currentWellScores = action.payload;
    },
    setScoringWell: (state, action: PayloadAction<ScoringWell[]>) => {
      const scoringWells = action.payload;
      scoringWells.forEach((scoringWell) => {
        const {
          plateId,
          wellNumber,
          dropNumber,
          imageBatchId,
          isDeleteScore,
          wellDropScoreGroupId,
          wellDropScoreId,
        } = scoringWell;
        if (!isDropIdNull(state?.expData?.plateDto, plateId, wellNumber, dropNumber)) {
          const allPlatesScores = state?.expData?.plateDto.find(
            (plate) => plate.plateId === plateId,
          )?.plateScores;
          const currWellScores =
            allPlatesScores?.length > 0
              ? allPlatesScores.find((scores) =>
                  scores.length > 0 ? scores[0]?.wellNumber === wellNumber : false,
                )
              : [];
          let allInspectionsScores = currWellScores?.length ? [...currWellScores] : [];
          const allScoreIndex = allInspectionsScores.findIndex(
            (wds) =>
              wds.wellNumber === wellNumber &&
              wds.wellDropScoreGroupId === wellDropScoreGroupId &&
              wds.scoreBatchId === imageBatchId &&
              wds.dropNumber === dropNumber,
          );
          let scoreObj = [];
          if (wellDropScoreGroupId === INTRESTINGSCOREID && !isDeleteScore) {
            const inspections = GetExpInspections();
            const dropInspections = [];
            inspections.forEach((inspection) => {
              inspection.groupedInspections.forEach((gi) =>
                dropInspections.push(gi.imageBatchId),
              );
            });
            const uniqueInspections = [...new Set(dropInspections)];
            uniqueInspections.forEach((di) =>
              scoreObj.push({
                wellNumber,
                wellDropScoreId,
                wellDropScoreGroupId,
                isAutoScore: false,
                scoreBatchId: di,
                dropNumber,
                scoreType: EScoreType.Manual,
                plateId: plateId,
              }),
            );
          } else {
            scoreObj = [
              {
                wellNumber,
                wellDropScoreId,
                wellDropScoreGroupId,
                isAutoScore: false,
                scoreBatchId: imageBatchId,
                dropNumber,
                scoreType: EScoreType.Manual,
                plateId: plateId,
              },
            ];
          }
          if (isDeleteScore && wellDropScoreGroupId === INTRESTINGSCOREID) {
            allInspectionsScores = allInspectionsScores.filter(
              (scores) =>
                scores.wellDropScoreGroupId !== INTRESTINGSCOREID ||
                scores.dropNumber !== dropNumber,
            );
          } else if (isDeleteScore && allScoreIndex !== -1) {
            allInspectionsScores.splice(allScoreIndex, 1);
          } else if (!isDeleteScore && wellDropScoreGroupId === INTRESTINGSCOREID) {
            const updated = [...allInspectionsScores];
            scoreObj.forEach((newScore) => {
              const idx = updated.findIndex(
                (s) =>
                  s.wellDropScoreGroupId === wellDropScoreGroupId &&
                  s.scoreBatchId === newScore.scoreBatchId &&
                  s.dropNumber === dropNumber &&
                  s.wellNumber === wellNumber,
              );
              if (idx !== -1) {
                updated.splice(idx, 1, newScore);
              } else {
                updated.push(newScore);
              }
            });
            allInspectionsScores = updated;
          } else if (allScoreIndex === -1) {
            // If selected well have scores from other score groups but not from the scoregroup which we have scored so it will be added in that well scores
            allInspectionsScores.push(scoreObj[0]);
          } else if (allScoreIndex !== -1) {
            allInspectionsScores.splice(allScoreIndex, 1, scoreObj[0]);
          }

          state.currentWellScores = allInspectionsScores;
          experimentDataSlice.caseReducers.setScoresOfCurrentWell(state, {
            type: 'setScoresOfCurrentWell',
            payload: {
              plateId: plateId,
              wellNum: wellNumber,
              wellScores: allInspectionsScores,
            },
          });
        }
      });
      state.scoringWell = state.scoringWell !== undefined ? !state.scoringWell : true;
    },

    setPlateSize: {
      reducer: (
        state,
        action: PayloadAction<{ plateWidth: number; plateHeight: number }>,
      ) => {
        const { plateWidth, plateHeight } = action.payload;
        const updatedPlateStructures = getPlateStructure(
          state?.expData?.plateDto,
          plateWidth,
          plateHeight,
          state?.expData?.containerRows,
          state?.expData?.containerCols,
        );
        if (updatedPlateStructures?.length) {
          const updatedPlates = state.expData.plateDto.map((plate, index) => {
            return { ...plate, ...updatedPlateStructures[index] } as Plate;
          });
          state.expData.plateDto = updatedPlates;
        }
      },
      prepare: prepareAutoBatched<{ plateWidth: number; plateHeight: number }>(),
    },

    prepareAndSetExperimentDataAfterSave: (
      state,
      action: PayloadAction<{
        saveResult: SaveExperimentInfo;
        layers: LayerDTO[];
        expInfo: ExperimentInfoDTO;
        imagingSchedule: ExperimentInfoImagingScheduleDTO;
      }>,
    ): void => {
      const saveResult = action.payload.saveResult;
      state.expData.layers.map((l) => ((l as DrawingLayer).isSaveAttempted = false));
      validateWellsAndDrops(
        saveResult.plates,
        state.expData.containerMaxDropVolume,
        saveResult.layers as LayerDTO[],
      );
      state.expData.isDirty = false;
      if (state.expData.info) {
        state.expData.info.isDirty = false;
      }
      if (action.payload && saveResult.inspections?.sets?.length > 0) {
        state.expData.inspections = saveResult.inspections?.sets;
      }
      state.expData.layers = action.payload.layers as DrawingLayer[];
      const oldPlates = [...state.expData.plateDto];
      state.expData.plateDto = [];
      saveResult.plates.forEach((_plate) => {
        const oldPlate = oldPlates.find((p) => p.plateNumber === _plate.plateNumber);
        state.expData.plateDto.push({
          ..._plate,
          plateScores: oldPlate.plateScores,
          drawingIdentifiers: oldPlate.drawingIdentifiers,
          imagingTasksEnabled: oldPlate.imagingTasksEnabled,
        } as Plate);
      });
      state.expData.dispensingProps = [];
      const { plateWidth, plateHeight } = getExperimentPlateSize(
        state.expData as ExperimentDtoClient,
      );
      generateEmptyCanvasItems(state.expData as ExperimentDtoClient);
      experimentDataSlice.caseReducers.setPlateSize(state, {
        type: 'setPlateSize',
        payload: { plateWidth, plateHeight },
      });
      state.expData.info = action.payload.expInfo as ExperimentInfoDto;
      state.expData.expName = (action.payload.expInfo as ExperimentInfoDTO).name;

      const plateLabels: LabelDto[] = createPlateLables(
        state.expData.plateDto,
        state.expData.colCount,
      );
      const { wellLabels } = getWellAndCanvasLabels(
        state.expData.containerCols,
        state.expData.containerRows,
      );
      state.expData.plateLables = plateLabels;
      state.expData.wellLables = wellLabels;
      state.expData.deletedPlateIds = [];
      state.expData.imagingSchedule = {
        ...action.payload.imagingSchedule,
      } as ExperimentInfoImagingScheduleDTO;
      state.expData.plateDto = state.expData.plateDto.sort(
        (x, y) => x.plateNumber - y.plateNumber,
      );
      experimentDataSlice.caseReducers.preserveExpData(state);
    },

    preserveExpData: (state) => {
      state.originalExpData = cloneDeep(state.expData);
    },
    discardExperimentChanges: (state, action: PayloadAction): any => {
      state.expData = cloneDeep(state.originalExpData);
      state.expData.isDirty = false;
      if (state.expData?.info) {
        state.expData.info.isDirty = false;
      }
    },
    removeInspection: (state, action: PayloadAction<number>) => {
      state.expData.inspections = state.expData.inspections.filter(
        (inspection) => inspection.id !== action.payload,
      );
    },
    appendInspection: (state, action: PayloadAction<InspectionSetDTO>) => {
      state.expData.inspections.push(action.payload);
    },
    setExperimentInfo: (state, action: PayloadAction<DetailExpInfoDTO>): any => {
      state.expData.info = { ...state.expData.info, ...action.payload };
    },
    deletePlatesByIds: (state, action: PayloadAction<number[]>): any => {
      action.payload.forEach((id) => {
        const plate = state.expData.plateDto.find((p) => p.plateId === id);
        if (plate.plateId > 0) {
          state.expData.deletedPlateIds.push(plate.plateId);
        }
        const index = state.expData.plateDto.findIndex((p) => p.plateId === id);
        state.expData.plateDto.splice(index, 1);
      });
    },
    updateExpPlateRowDefWellVolume: (state, action: PayloadAction<number>): any => {
      state.expData.rowCount = action.payload;
      const containers = GetContainers() as Containers[];
      const cont = containers.find((c) => c.id == state.expData.info.containerId);
      const wellCount = cont.rows * cont.cols;
      for (let row = state.expData.info.plateRowCount + 1; row <= action.payload; row++) {
        for (let col = 1; col <= state.expData.colCount; col++) {
          const plateNum = (row - 1) * state.expData.colCount + col;
          const newPlate = getNewPlate(plateNum, cont, row, col, wellCount);
          state.expData.plateDto.push(newPlate);
        }
      }
      state.expData.defWellVolume = cont.defaultWellVolume;
    },
    updatePlateDTOandLayers: (state, action: PayloadAction): any => {
      state.expData.plateDto.forEach((plate) => {
        plate.plateNumber = (plate.rowPos - 1) * state.expData.colCount + plate.colPos;
        if (plate.plateId < 0) {
          plate.plateBarName = 'Plate ' + plate.plateNumber;
        }
      });
      state.expData.plateDto.sort(function (a, b) {
        return a.plateNumber - b.plateNumber;
      });
      state.expData.info.isDirty = true;
      state.expData.isDirty = true;
      state.expData.layers &&
        state.expData.layers.forEach((layer) => (layer.dirty = true));
    },
    updateExpPlateColDefaultWellVolume: (state, action: PayloadAction<number>): any => {
      state.expData.colCount = action.payload;
      const containers = GetContainers() as Containers[];
      const cont = containers.find((c) => c.id == state.expData.info.containerId);
      const wellCount = cont.rows * cont.cols;
      for (let row = 1; row <= state.expData.info.plateRowCount; row++) {
        for (
          let col = state.expData.info.plateColCount + 1;
          col <= action.payload;
          col++
        ) {
          const plateNum = (row - 1) * state.expData.colCount + col;
          const newPlate = getNewPlate(plateNum, cont, row, col, wellCount);
          state.expData.plateDto.push(newPlate);
        }
      }
      state.expData.defWellVolume = cont.defaultWellVolume;
    },
    filterExpLayers: (state, action: PayloadAction<LayerDTO[]>): any => {
      state.expData.layers = action.payload;
    },
    setExperiment: (state, action: PayloadAction<DetailExperimentDTO>): any => {
      state.expData = { ...state.expData, ...action.payload };
    },
    dirtifyLayers: (state, action: PayloadAction): any => {
      state.expData.layers.forEach((lyr) => (lyr.dirty = true));
    },
    pruneWellDropsToContainer: (state, action: PayloadAction<ContainerDrop[]>): void => {
      const dropNumbers = new Set(action.payload.map((d) => d.dropNumber));
      state.expData.plateDto.forEach((plate) => {
        plate.ingredients.wellList.forEach((well) => {
          well.dropList = well.dropList.filter((drop) =>
            dropNumbers.has(drop.dropNumber),
          );
        });
      });
    },
    updateExpInfoScreenLots: (state, action: PayloadAction<number>): any => {
      state.expData.info.screenLots.forEach((sl) => {
        sl.containerId = action.payload;
        sl.state = State.Updated;
      });
    },
    upsertImagingSchedule: (
      state,
      action: PayloadAction<{ imagingScheduleDetails: ImagingScheduleUpdateDTO }>,
    ): void => {
      state.expData.imagingSchedule = {
        ...state.expData.imagingSchedule,
        ...action.payload.imagingScheduleDetails,
      };
    },
    appendLayer: (state, action: PayloadAction<DrawingLayer>) => {
      state.expData.layers.push(action.payload);
      state.expData.isDirty = true;
    },
    processLayerCreation: (state, action: PayloadAction) => {
      if (state.expData.layers.findIndex((l) => l.dirty === true) >= 0) {
        state.expData.isDirty = true;
      }
      experimentDataSlice.caseReducers.generateWellContents(state, {
        type: 'experiment/generate',
        payload: false,
      });
      experimentDataSlice.caseReducers.unDirtifyAlllayers(state, {
        type: 'experiment/unDirtifyAlllayers',
        payload: null,
      });
      for (const plate of state.expData.plateDto) {
        plate.ingredients = { ...plate.ingredients } as PlateIngredient;
      }
      state.expData.plateDto = getPlainArray(state.expData.plateDto);
      const selectedLayer = state.expData.layers.find(
        (l) => l.id === state.selectedLayerId,
      );
      if (!selectedLayer && state.expData.layers.length > 0) {
        state.selectedLayerId = state.expData.layers[0].id;
      }
    },

    generateWellContents: (state, action: PayloadAction<boolean>) => {
      generate(state.expData as ExperimentDtoClient, action.payload);
    },

    deleteLayers: (
      state,
      action: PayloadAction<{ layerToDelete: LayerDTO; deleteFamily: boolean }>,
    ) => {
      const { layerToDelete, deleteFamily } = action.payload;
      state.expData.layers = state.expData.layers.map((lyr) =>
        dirtifyOverLappingLayers(lyr as LayerDTO, layerToDelete),
      );
      if (state.expData.layers.findIndex((lyr) => lyr.id == layerToDelete.id) < 0) {
        return;
      }
      unGenerateLayer(state.expData as ExperimentDtoClient, layerToDelete);
      for (const plate of state.expData.plateDto) {
        plate.ingredients = { ...plate.ingredients } as PlateIngredient;
      }
      const tempLayers = cloneDeep(state.expData.layers);
      const deletedLayerIndex = tempLayers.findIndex(
        (layer) => layer.id === layerToDelete.id,
      );
      let linkedLayers = [];
      if (deletedLayerIndex != -1) {
        tempLayers.splice(deletedLayerIndex, 1);
        if (layerToDelete.type === LayerTypeDTO.Grid) {
          linkedLayers = getLinkedLayers(tempLayers as DrawingLayer[], layerToDelete);
          if (!isNullOrUndefined(linkedLayers) && linkedLayers.length > 0) {
            makeNewParentLinkedLayer(
              linkedLayers,
              layerToDelete.layerDetail as GridLayerDTO,
            );
            if (deleteFamily) {
              linkedLayers.forEach((layer) => {
                unGenerateLayer(state.expData as ExperimentDtoClient, layer);
                const deleteIndex = tempLayers.findIndex(
                  (tLayer) => tLayer.id === layer.id,
                );
                tempLayers.splice(deleteIndex, 1);
              });
            }
          }
        }
      }
      state.expData.layers = tempLayers;
      state.expData.isDirty = true;
      const selectedLayerId = getNewSelectedLayerAfterLayerDeletion(
        linkedLayers,
        tempLayers as LayerDTO[],
        layerToDelete,
        deletedLayerIndex,
      );
      if (selectedLayerId) {
        experimentDataSlice.caseReducers.setSelectedLayerId(state, {
          type: 'experiment/setSelectedLayerId',
          payload: selectedLayerId,
        });
      }
    },
    pasteNewLayer: (
      state,
      action: PayloadAction<{
        layerType: LayerTypeDTO;
        selectedWells: CopyWellDetail[];
        wellNumber: number;
      }>,
    ): any => {
      state.expData = pasteNewLayerUtil(
        state.expData,
        action.payload.layerType,
        action.payload.selectedWells,
        action.payload.wellNumber,
      );
    },
    expandPlateRowInImport: (state, action: PayloadAction<number>) => {
      state.expData.rowCount = action.payload;
      const containers = GetContainers() as Containers[];
      const cont = containers.find((c) => c.id == state.expData.info.containerId);
      const wellCount = cont.rows * cont.cols;
      for (let row = state.expData.info.plateRowCount + 1; row <= action.payload; row++) {
        for (let col = 1; col <= state.expData.colCount; col++) {
          const plateNum = (row - 1) * state.expData.colCount + col;
          const newPlate = getNewPlate(plateNum, cont, row, col, wellCount);
          state.expData.plateDto.push(newPlate);
        }
      }
      state.expData.plateDto.forEach((plate) => {
        plate.plateNumber = (plate.rowPos - 1) * state.expData.colCount + plate.colPos;
        if (plate.plateId < 0) {
          plate.plateBarName = 'Plate ' + plate.plateNumber;
        }
      });
      state.expData.plateDto.sort(function (a, b) {
        return a.plateNumber - b.plateNumber;
      });
      state.expData.info.plateRowCount = action.payload;
      state.expData.info.isDirty = true;
      state.expData.rowCount = action.payload;
      state.expData.isDirty = true;
      for (const plate of state.expData.plateDto) {
        plate.ingredients = cloneDeep(plate.ingredients) as PlateIngredient;
      }
      state.expData.layers &&
        state.expData.layers.forEach((layer) => (layer.dirty = true));
      const { plateWidth, plateHeight } = getExperimentPlateSize(
        state.expData as ExperimentDtoClient,
      );
      experimentDataSlice.caseReducers.setPlateSize(state, {
        type: 'setPlateSize',
        payload: { plateWidth, plateHeight },
      });
    },
    addScreenLot: (state, action: PayloadAction<IExperimentScreenLot>) => {
      action.payload.state = State.New;
      const screenLots = [...state.expData.info.screenLots, action.payload];
      state.expData.info.screenLots = screenLots;
    },
    updateScreenLot: (state, action: PayloadAction<IExperimentScreenLot>) => {
      action.payload.state = State.Updated;
      if (action.payload.id < 0) {
        action.payload.state = State.New;
      }

      const updatedScreenLots = [...state.expData.info.screenLots].map((lot) => {
        if (lot.id === action.payload.id) {
          return { ...action.payload } as IExperimentScreenLot;
        }
        return lot;
      });

      state.expData.info.screenLots = updatedScreenLots;
    },
    deleteScreenLot: (state, action: PayloadAction<IExperimentScreenLot>) => {
      action.payload.state = State.Deleted;
      const screenLots =
        action.payload.id > 0
          ? [
              ...state.expData.info.screenLots.filter(
                (s) => s.lotNumber !== action.payload.lotNumber,
              ),
              action.payload,
            ]
          : state.expData.info.screenLots.filter(
              (s) => s.lotNumber !== action.payload.lotNumber,
            );
      state.expData.info.screenLots = screenLots;
    },

    applyDoeIngredients: (state, action: PayloadAction<any>) => {
      applyDoeIngredientsData(state.expData, state.selectedLayerId, action.payload);
    },

    markDisputedScores: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        disputedScoreGroupsIds: DisputedScoresIds[];
      }>,
    ) => {
      const { plateId, wellNumber, disputedScoreGroupsIds } = action.payload;

      const allPlatesScores = state?.expData?.plateDto.find(
        (plate) => plate.plateId === plateId,
      )?.plateScores;
      const currWellScores =
        allPlatesScores?.length > 0
          ? allPlatesScores.find((scores) =>
              scores.length > 0 ? scores[0]?.wellNumber === wellNumber : false,
            )
          : [];

      disputedScoreGroupsIds.forEach((disputedScoreGroupIds) => {
        const updatedScores = currWellScores.map((score) => {
          if (
            score.imageBatchWellDropScoreId ===
            disputedScoreGroupIds.imageBatchWellDropScoreID
          ) {
            return { ...score, isDisputed: true };
          } else {
            return score;
          }
        });
        state.currentWellScores = updatedScores;
        experimentDataSlice.caseReducers.setScoresOfCurrentWell(state, {
          type: 'setScoresOfCurrentWell',
          payload: {
            plateId: plateId,
            wellNum: wellNumber,
            wellScores: updatedScores,
          },
        });
      });
    },
    setAutoScoreIgnoredStatus: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        imageBatchId: number;
        dropNumber: number;
        isAutoScoreIgnored: boolean;
      }>,
    ) => {
      const { plateId, wellNumber, imageBatchId, dropNumber, isAutoScoreIgnored } =
        action.payload;
      const allPlatesScores = state?.expData?.plateDto.find(
        (plate) => plate.plateId === plateId,
      )?.plateScores;
      const currWellScores =
        allPlatesScores?.length > 0
          ? allPlatesScores.find((scores) =>
              scores.length > 0 ? scores[0]?.wellNumber === wellNumber : false,
            )
          : [];

      if (!currWellScores?.length) {
        return;
      }

      const updatedScores = currWellScores.map((score) =>
        score.isAutoScore === true &&
        score.scoreBatchId === imageBatchId &&
        score.dropNumber === dropNumber
          ? { ...score, isAutoScoreIgnored }
          : score,
      );
      state.currentWellScores = updatedScores;
      experimentDataSlice.caseReducers.setScoresOfCurrentWell(state, {
        type: 'setScoresOfCurrentWell',
        payload: {
          plateId,
          wellNum: wellNumber,
          wellScores: updatedScores,
        },
      });
    },
    togglePlateImagingTasksEnabled: (
      state,
      action: PayloadAction<{ plates: PlatesImagingTaskDTO[] }>,
    ) => {
      const { plates } = action.payload;
      const updatedPlates = [...state.expData.plateDto];
      plates.forEach((plate) => {
        const plateIndex = updatedPlates.findIndex((p) => p.plateId === plate.plateId);
        if (plateIndex !== -1) {
          updatedPlates[plateIndex].imagingTasksEnabled = plate.imagingEnabled;
        }
      });
      state.expData.plateDto = updatedPlates;
    },
    updateLayerProperties: (state, action: PayloadAction<{ id: number; props: any }>) => {
      const selectedLayerIndex = state.expData.layers.findIndex(
        (lyr) => lyr.id === action.payload.id,
      );
      if (selectedLayerIndex >= 0) {
        state.expData.layers[selectedLayerIndex] = {
          ...state.expData.layers[selectedLayerIndex],
          ...action.payload.props,
        };
      }
    },
    toggleImportFl: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        importFl: boolean;
      }>,
    ) => {
      const { plateId, wellNumber, dropNumber, importFl } = action.payload;
      const plates = [...state.expData.plateDto];

      const plate = plates.find((plate) => plate.plateId === plateId);
      if (!plate) {
        return;
      }

      const well = plate.ingredients.wellList.find(
        (well) => well.wellNumber === wellNumber,
      );
      if (!well) {
        return;
      }

      const wellDrop = well.dropList.find((drop) => drop.dropNumber === dropNumber);
      if (!wellDrop) {
        return;
      }

      // Set explicit value (do not flip null/undefined) so local state matches the API payload
      // when drop-level importFl was unset and the UI fell back to the capture profile default.
      wellDrop.importFl = importFl;
      state.expData.plateDto = plates;
    },
    appendAndUpdatePlateDrawingIcons: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        drawingType: DrawingType;
      }>,
    ) => {
      const { plateId, wellNumber, dropNumber, drawingType } = action.payload;
      const plateDrawing: Annotations = state?.expData?.plateDto?.find(
        (x) => x.plateId === plateId,
      )?.drawingIdentifiers;

      if (!plateDrawing) {
        return;
      }

      const newIconDetail = { wellNumber, dropNumber } as WellDrawingDetailDTO;
      const targetArray =
        drawingType === DrawingType.Annotation
          ? plateDrawing.plateAnnotations
          : plateDrawing.plateROI;
      const exists = targetArray.some(
        (detail) => detail.wellNumber === wellNumber && detail.dropNumber === dropNumber,
      );

      if (!exists) {
        targetArray.push(newIconDetail);
      }
    },
    appendAndUpdateCrystalIdentifiers: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        regionName: string;
        cpId: number;
        imageBatchId: number;
      }>,
    ) => {
      const { plateId, wellNumber, dropNumber, regionName, cpId, imageBatchId } =
        action.payload;
      const plateDrawing: Annotations = state?.expData?.plateDto?.find(
        (x) => x.plateId === plateId,
      )?.drawingIdentifiers;

      if (!plateDrawing) {
        return;
      }

      const newIconDetail = {
        wellNumber,
        dropNumber,
        regionName,
        cpId,
        imageBatchId,
        plateId,
      } as IPlateCrystalIdentifiers;
      const targetArray = plateDrawing.plateCrystalIdentifiers;
      const exists = targetArray.some(
        (detail) => detail.wellNumber === wellNumber && detail.dropNumber === dropNumber,
      );

      if (!exists) {
        targetArray.push(newIconDetail);
      }
    },
    removeFromPlateDrawing: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        drawingType: DrawingType;
      }>,
    ) => {
      const { plateId, wellNumber, dropNumber, drawingType } = action.payload;
      const plate = state?.expData?.plateDto?.find((x) => x.plateId === plateId);
      const plateDrawing = plate?.drawingIdentifiers;

      if (!plateDrawing) {
        return;
      }
      const targetArray =
        drawingType === DrawingType.Annotation
          ? plateDrawing.plateAnnotations
          : plateDrawing.plateROI;
      const drawingIndex = targetArray.findIndex(
        (detail) => detail.wellNumber === wellNumber && detail.dropNumber === dropNumber,
      );
      if (drawingIndex >= 0) {
        targetArray.splice(drawingIndex, 1);
      }
      plate.drawingIdentifiers = { ...plate.drawingIdentifiers };
    },
    setPlateDrawingIdentifiers: (
      state,
      action: PayloadAction<PlateDrawingIdentifiers[]>,
    ) => {
      const drawingIdentifiers = action.payload;
      state.expData.plateDto.forEach((plate) => {
        const currentPlateDrawings = drawingIdentifiers.find(
          (x) => x.plateId == plate.plateId,
        );
        plate.drawingIdentifiers = currentPlateDrawings.drawingsIdentifier;
      });
    },
    setPlatesCompositeImages: (state, action: PayloadAction<PlateCompositeImage[]>) => {
      const compositeImages = action.payload;
      state.expData.plateDto.forEach((plate) => {
        const compositeImage = compositeImages.find((x) => x.plateId == plate.plateId);
        plate.image = compositeImage.compositeImageKey;
      });
    },
    setPlateImagesStructure: (state, action: PayloadAction<PlateImageStructure[]>) => {
      const plateImageStructure = action.payload;
      state.plateImageStructure = plateImageStructure;
    },
    setWellDropsImagingEnabled: (
      state,
      action: PayloadAction<{
        wellDropRegions: { plateId: number; wellNumber: number; dropNumber: number }[];
        imagingEnabled: boolean;
      }>,
    ) => {
      const { wellDropRegions, imagingEnabled } = action.payload;
      wellDropRegions.map((wellDropRegion) => {
        const plate = state?.expData?.plateDto?.find(
          (x) => x.plateId == wellDropRegion.plateId,
        );
        const drawingIdentifiers = plate.drawingIdentifiers.plateDropRegions.filter(
          (x) =>
            x.dropNumber == wellDropRegion.dropNumber &&
            x.wellNumber == wellDropRegion.wellNumber,
        );
        if (drawingIdentifiers?.length) {
          drawingIdentifiers.forEach((x) => (x.imagingEnabled = imagingEnabled));
        }
      });
    },
    togglePlateImaging: (
      state,
      action: PayloadAction<{ plates: PlatesImagingTaskDTO[] }>,
    ) => {
      const { plates } = action.payload;
      plates.map((plate) => {
        const plateToUpdate = state.expData.plateDto.find(
          (x) => x.plateId == plate.plateId,
        );
        plateToUpdate.drawingIdentifiers?.plateDropRegions.forEach(
          (x) => (x.imagingEnabled = plate.imagingEnabled),
        );
        plateToUpdate.imagingTasksEnabled = plate.imagingEnabled;
      });
    },
    updateCanvasScribbleDrawing: (
      state,
      action: PayloadAction<{ plateId: number; scribbles: PlateScribble }>,
    ) => {
      const { plateId, scribbles } = action.payload;
      const plate = state?.expData?.plateDto?.find((x) => x.plateId == plateId);
      if (plate) {
        plate.drawingIdentifiers.plateScribblesDetail = scribbles;
      }
    },

    removeCanvasScribbleDrawing: (
      state,
      action: PayloadAction<{
        plateId: number;
        wellNumber: number;
        dropNumber: number;
        drawingId: number;
      }>,
    ) => {
      const { plateId, wellNumber, dropNumber, drawingId } = action.payload;

      const plate = state?.expData?.plateDto?.find((x) => x.plateId === plateId);
      if (!plate) {
        return;
      }

      const scribbles = plate.drawingIdentifiers.plateScribblesDetail.plateScribbles;

      const scribbleIndex = scribbles.findIndex(
        (scribble) => scribble.scribbleStartPointsXYofWell.id === drawingId,
      );

      if (scribbleIndex >= 0) {
        scribbles.splice(scribbleIndex, 1);
      }

      const containingScribbles =
        plate.drawingIdentifiers.plateScribblesDetail.wellNumsContainingScribbles;

      const containingIndex = containingScribbles.findIndex(
        (item) => item.wellNum === wellNumber && item.dropNum === dropNumber,
      );

      if (containingIndex >= 0) {
        containingScribbles.splice(containingIndex, 1);
      }

      plate.drawingIdentifiers = { ...plate.drawingIdentifiers };
    },
    setWellSelection: (state, action: PayloadAction<WellSelectionDetails>) => {
      state.wellSelectionDetails = action.payload;
    },
    setWellNavigationDetails: (state, action: PayloadAction<WellNavigationDetails[]>) => {
      state.wellSelectionDetails.wellNavigationDetails = action.payload;
    },
    appendWellNavigationDetails: (
      state,
      action: PayloadAction<WellNavigationDetails>,
    ) => {
      state.wellSelectionDetails.wellNavigationDetails.push(action.payload);
    },
    setWellImagingSettingDetails: (
      state,
      action: PayloadAction<DropImageParameters[]>,
    ) => {
      state.wellSelectionDetails.dropImageParameters = action.payload;
      state.wellSelectionDetails.selectedDropImageParameters =
        action.payload[action.payload.length - 1];
    },
    appendWellImagingSettingDetails: (
      state,
      action: PayloadAction<DropImageParameters>,
    ) => {
      state.wellSelectionDetails.dropImageParameters.push(action.payload);
      state.wellSelectionDetails.selectedDropImageParameters = action.payload;
    },
    selectWellImagingSettingDetails: (
      state,
      action: PayloadAction<DropImageParameters>,
    ) => {
      state.wellSelectionDetails.selectedDropImageParameters = action.payload;
    },
    switchWellImagingSettingDetails: (state) => {
      const arr = state.wellSelectionDetails.dropImageParameters;
      if (arr.length <= 1) {
        state.wellSelectionDetails.selectedDropImageParameters =
          arr[arr.length - 1] ?? null;
        return;
      }
      const lastIndex = arr.length - 1;
      const updated = arr.swapItemsByIndex(lastIndex - 1, lastIndex);
      state.wellSelectionDetails.dropImageParameters = updated;
      state.wellSelectionDetails.selectedDropImageParameters = updated[lastIndex];
    },
    updateSelectedWellInspection: (state, action: PayloadAction<InspectionSetDTO>) => {
      state.wellSelectionDetails.selectedDropImageParameters.inspection = action.payload;
      state.wellSelectionDetails.dropImageParameters.forEach((dropImageParameter) => {
        dropImageParameter.inspection = action.payload;
      });
    },
    updateLayerName: (state, action: PayloadAction<{ id: number; name: string }>) => {
      const layersCopy: DrawingLayer[] = cloneDeep(state.expData.layers);

      const layerIndex = layersCopy.findIndex((l) => l.id === action.payload.id);
      if (layerIndex !== -1) {
        layersCopy[layerIndex].name = action.payload.name;
        layersCopy[layerIndex].dirty = true;
      }

      // need to mark overriden layers dirty in order to be re-generated and calculate correct well volume
      layersCopy.forEach((l) => (l.overrideLayer ? (l.dirty = true) : null));

      state.expData.layers = layersCopy;
    },
    appendHistogramIdentifiers: (state, action: PayloadAction<WellDetailsStates>) => {
      const { wellNum, dropNumber, cpID, imageBatchId, regionName, plateID } =
        action.payload;
      const plate = state.expData.plateDto.find((p) => p.plateId === plateID);
      if (!plate?.drawingIdentifiers?.plateHistograms) {
        return;
      }

      const exists = plate.drawingIdentifiers.plateHistograms.some(
        (x) =>
          x.regionName === regionName &&
          x.dropNumber === dropNumber &&
          x.cpId === cpID &&
          x.imageBatchId === imageBatchId &&
          x.wellNumber === wellNum,
      );
      if (!exists) {
        plate.drawingIdentifiers.plateHistograms.push({
          regionName: regionName,
          dropNumber: dropNumber,
          cpId: cpID,
          imageBatchId: imageBatchId,
          wellNumber: wellNum,
          plateId: plateID,
        });
      }
    },
    setAllowLayerEditing: (state, action: PayloadAction<boolean>) => {
      state.allowLayerEditing = action.payload;
    },
    setIsUpdatingGlobalData: (state, action: PayloadAction<boolean>) => {
      state.isUpdatingGlobalData = action.payload;
    },
    setIsFetchingExperimentData: (state, action: PayloadAction<boolean>) => {
      state.isFetchingExperimentData = action.payload;
    },
    appendScoreExperimentWellsInProgressCount: (state, action: PayloadAction<number>) => {
      state.scoreExperimentWellsInProgressCount += action.payload;
    },
    setIsLoadingComposite: (state, action: PayloadAction<boolean>) => {
      state.isLoadingComposite = action.payload;
    },
    setOriginalExpData: (state) => {
      if (!state.originalExpData || !Object.keys(state.originalExpData).length) {
        state.originalExpData = cloneDeep(state.expData);
      }
    },
    removeHistogramIdentifiers: (state, action: PayloadAction<WellDetailsStates>) => {
      const { wellNum, dropNumber, cpID, imageBatchId, regionName, plateID } =
        action.payload;

      const plate = state.expData.plateDto.find((p) => p.plateId === plateID);

      if (!plate?.drawingIdentifiers?.plateHistograms) {
        return;
      }

      plate.drawingIdentifiers.plateHistograms =
        plate.drawingIdentifiers.plateHistograms.filter(
          (x) =>
            !(
              x.regionName === regionName &&
              x.dropNumber === dropNumber &&
              x.cpId === cpID &&
              x.imageBatchId === imageBatchId &&
              x.wellNumber === wellNum
            ),
        );
    },
  },
});

export const {
  updateExperimentDataState,
  updateExperimentLayers,
  updateSelectedLayer,
  updateLayerGroupData,
  replaceSelectedLayerDetail,
  setSelectedLayerId,
  setUnselectLayer,
  setExpPlates,
  setExpRegionCaptureProfiles,
  setExpPlateImage,
  updateLayersCanvasData,
  unDirtifyAlllayers,
  setDropImageUrl,
  setExpTuningValues,
  setIsDropImageAvailable,
  setSelectOverlay,
  setDrawingMoveToBack,
  setIsLockDrawing,
  setExpPlate,
  dirtifyExperiment,
  setPlateTunedImage,
  setScoreOptionsList,
  preserveUndoableState,
  performUndo,
  performRedo,
  setIsLayerSelected,
  clearUndoRedoStack,
  setExpPlateTilesCoords,
  setShouldShowLayerIngredientPanel,
  setShouldShowLayerEditPanel,
  setPlatesAtImagerStatus,
  setMotherPlatesScoringStatuses,
  setDispensingRobotId,
  setPlateLabels,
  setWellLabels,
  setAllPlatesScores,
  updatePlateBarcodes,
  setScoresOfCurrentWell,
  toggleScoringWell,
  setCurrentWellScores,
  setScoringWell,
  prepareAndSetExperimentDataAfterSave,
  preserveExpData,
  discardExperimentChanges,
  removeInspection,
  setPlateSize,
  setExperimentInfo,
  deletePlatesByIds,
  updateExpPlateRowDefWellVolume,
  updatePlateDTOandLayers,
  updateExpPlateColDefaultWellVolume,
  filterExpLayers,
  setExperiment,
  dirtifyLayers,
  pruneWellDropsToContainer,
  updateExpInfoScreenLots,
  appendInspection,
  upsertImagingSchedule,
  appendLayer,
  processLayerCreation,
  generateWellContents,
  deleteLayers,
  pasteNewLayer,
  expandPlateRowInImport,
  addScreenLot,
  updateScreenLot,
  deleteScreenLot,
  applyDoeIngredients,
  markDisputedScores,
  setAutoScoreIgnoredStatus,
  togglePlateImagingTasksEnabled,
  updateLayerProperties,
  toggleImportFl,
  appendAndUpdatePlateDrawingIcons,
  removeFromPlateDrawing,
  setPlateDrawingIdentifiers,
  setPlatesCompositeImages,
  setPlateImagesStructure,
  setWellDropsImagingEnabled,
  togglePlateImaging,
  updateCanvasScribbleDrawing,
  removeCanvasScribbleDrawing,
  setWellSelection,
  setWellNavigationDetails,
  appendWellNavigationDetails,
  setWellImagingSettingDetails,
  appendWellImagingSettingDetails,
  selectWellImagingSettingDetails,
  updateSelectedWellInspection,
  updateLayerName,
  appendHistogramIdentifiers,
  setAllowLayerEditing,
  setIsUpdatingGlobalData,
  setIsFetchingExperimentData,
  appendScoreExperimentWellsInProgressCount,
  setIsLoadingComposite,
  appendAndUpdateCrystalIdentifiers,
  setOriginalExpData,
  removeHistogramIdentifiers,
  switchWellImagingSettingDetails,
} = experimentDataSlice.actions;

export default experimentDataSlice.reducer;

const isDropIdNull = (
  plates: any,
  plateId: number,
  wellNumber: number,
  dropNumber: number,
) => {
  const plate = plates?.find((plate) => plate.plateId === plateId);
  if (!plate) {
    return true;
  }

  const well = plate.ingredients?.wellList?.find(
    (well) => well.wellNumber === wellNumber,
  );
  if (well) {
    const drop = well.dropList?.find((drop) => drop.dropNumber === dropNumber);
    if (drop && drop.dropId !== null && drop.dropId > 0) {
      return false;
    }
  }
  return true;
};

const compositeImagesStoreInMemory = {};
export const getCompositeImageStoreImageByKey = (key: string) => {
  return compositeImagesStoreInMemory[key];
};

export const getCompositeImageStoreObject = () => {
  return compositeImagesStoreInMemory;
};

export const setComposteImageStoreImageByKey = (key: string, image: any) => {
  if (!compositeImagesStoreInMemory[key]) {
    return (compositeImagesStoreInMemory[key] = image);
  }
};

export const setReplaceComposteImageStoreImageByKey = (key: string, image: any) => {
  if (compositeImagesStoreInMemory[key]) {
    return (compositeImagesStoreInMemory[key] = image);
  }
};

const navigatorImages = {};
export const getNavigatorImageStoreByKey = (key: string) => {
  return navigatorImages[key];
};

export const getNavigatorImageStoreObject = () => {
  return navigatorImages;
};

export const setNavigatorImageStoreByKey = (key: string, data: NavigatorImagesData) => {
  return (navigatorImages[key] = data);
};
